// src/store/slices/disputasSlice.ts

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  Disputa,
  getDisputasAdmin,
  resolverDisputa,
  cerrarDisputa,
  cambiarEstadoDisputa,
  ResolverDisputaRequest,
  EstadoDisputa,
} from "../../api/disputaApi";

interface DisputasState {
  items: Disputa[];
  totalPages: number;
  totalElements: number;
  currentPage: number;
  filtroEstado: string;
  loading: boolean;
  actionLoading: string | null;
  error: string | null;
  successMsg: string | null;
  disputasAbiertas: number;
}

const initialState: DisputasState = {
  items: [],
  totalPages: 0,
  totalElements: 0,
  currentPage: 0,
  filtroEstado: "",
  loading: false,
  actionLoading: null,
  error: null,
  successMsg: null,
  disputasAbiertas: 0,
};

// ── Thunks ────────────────────────────────────────────────────────────────────

export const fetchDisputas = createAsyncThunk(
  "disputas/fetch",
  async (
    { estado, page }: { estado?: string; page?: number },
    { rejectWithValue },
  ) => {
    try {
      const res = await getDisputasAdmin(estado, page ?? 0, 20);
      return res.content;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.mensaje || "Error al cargar disputas",
      );
    }
  },
);

export const resolverDisputaThunk = createAsyncThunk(
  "disputas/resolver",
  async (
    { id, request }: { id: string; request: ResolverDisputaRequest },
    { rejectWithValue },
  ) => {
    try {
      const res = await resolverDisputa(id, request);
      return res.content!;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.mensaje || "Error al resolver",
      );
    }
  },
);

export const cerrarDisputaThunk = createAsyncThunk(
  "disputas/cerrar",
  async (
    { id, motivo }: { id: string; motivo?: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await cerrarDisputa(id, motivo);
      return res.content!;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.mensaje || "Error al cerrar");
    }
  },
);

export const cambiarEstadoDisputaThunk = createAsyncThunk(
  "disputas/cambiarEstado",
  async (
    { id, nuevoEstado }: { id: string; nuevoEstado: EstadoDisputa },
    { rejectWithValue },
  ) => {
    try {
      const res = await cambiarEstadoDisputa(id, nuevoEstado);
      return res.content!;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.mensaje || "Error al cambiar estado",
      );
    }
  },
);

// ── Slice ─────────────────────────────────────────────────────────────────────

const disputasSlice = createSlice({
  name: "disputas",
  initialState,
  reducers: {
    setFiltroEstado(state, action) {
      state.filtroEstado = action.payload;
      state.currentPage = 0;
    },
    setPage(state, action) {
      state.currentPage = action.payload;
    },
    clearMessages(state) {
      state.error = null;
      state.successMsg = null;
    },
    setDisputasAbiertas(state, action) {
      state.disputasAbiertas = action.payload;
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchDisputas.pending, (s) => {
      s.loading = true;
      s.error = null;
    });
    b.addCase(fetchDisputas.fulfilled, (s, a) => {
      s.loading = false;
      const p = a.payload as any;
      if (p) {
        s.items = p.content ?? p.items ?? [];
        s.totalPages = p.totalPages ?? 0;
        s.totalElements = p.totalElements ?? 0;
      }
    });
    b.addCase(fetchDisputas.rejected, (s, a) => {
      s.loading = false;
      s.error = a.payload as string;
    });

    b.addCase(resolverDisputaThunk.pending, (s, a) => {
      s.actionLoading = a.meta.arg.id;
    });
    b.addCase(resolverDisputaThunk.fulfilled, (s, a) => {
      s.actionLoading = null;
      const fallo = a.payload.favorDe;
      if (fallo === 'CLIENTE') {
        s.successMsg = '✅ Disputa resuelta a favor del CLIENTE — el saldo de ese servicio queda bloqueado para el MVZ.';
      } else if (fallo === 'MVZ') {
        s.successMsg = '✅ Disputa resuelta a favor del MVZ — el MVZ puede retirar el saldo del servicio.';
      } else {
        s.successMsg = '✅ Disputa resuelta (mediación) — sin impacto financiero para ninguna parte.';
      }
      const idx = s.items.findIndex((d) => d.id === a.payload.id);
      if (idx !== -1) s.items[idx] = a.payload;
    });
    b.addCase(resolverDisputaThunk.rejected, (s, a) => {
      s.actionLoading = null;
      s.error = a.payload as string;
    });

    b.addCase(cerrarDisputaThunk.pending, (s, a) => {
      s.actionLoading = a.meta.arg.id;
    });
    b.addCase(cerrarDisputaThunk.fulfilled, (s, a) => {
      s.actionLoading = null;
      s.successMsg = "🔒 Disputa cerrada";
      const idx = s.items.findIndex((d) => d.id === a.payload.id);
      if (idx !== -1) s.items[idx] = a.payload;
    });
    b.addCase(cerrarDisputaThunk.rejected, (s, a) => {
      s.actionLoading = null;
      s.error = a.payload as string;
    });

    b.addCase(cambiarEstadoDisputaThunk.pending, (s, a) => {
      s.actionLoading = a.meta.arg.id;
    });
    b.addCase(cambiarEstadoDisputaThunk.fulfilled, (s, a) => {
      s.actionLoading = null;
      const estado = a.payload.estado;
      s.successMsg =
        estado === "EN_REVISION"
          ? "🔍 Disputa marcada como En Revisión — ambas partes fueron notificadas por push"
          : `✅ Estado actualizado a ${estado}`;
      const idx = s.items.findIndex((d) => d.id === a.payload.id);
      if (idx !== -1) s.items[idx] = a.payload;
    });
    b.addCase(cambiarEstadoDisputaThunk.rejected, (s, a) => {
      s.actionLoading = null;
      s.error = a.payload as string;
    });
  },
});

export const { setFiltroEstado, setPage, clearMessages, setDisputasAbiertas } =
  disputasSlice.actions;
export default disputasSlice.reducer;
